import type { CurrencyCode as LocaleCurrencyCode } from "@lunagoai/locale-data";

export type CurrencyCode = LocaleCurrencyCode;
export type PaymentMethod = "cash" | "card" | "mixed";
export type CardPaymentMode = "manual";
export type DiscountType = "none" | "10off" | "20off";
export type RefundMethod = "cash" | "card";
export type OrderStatus = "completed" | "voided";
export type CorrectionType = "void" | "refund";
export type PrintJobKind = "receipt" | "kitchen";
export type PrintJobStatus = "pending" | "printed" | "failed";
export type PrintCopiesMode = "none" | "kitchen" | "both";
export type { Permission, Role, TenantContext } from "./identity";

export type MenuOption = {
  id: string;
  name: string;
  priceMinor: number;
  active: boolean;
};

export type MenuOptionGroup = {
  id: string;
  name: string;
  required?: boolean;
  multiple?: boolean;
  options: MenuOption[];
};

export type MenuItem = {
  id: string;
  name: string;
  category: string;
  priceMinor: number;
  currency: CurrencyCode;
  active: boolean;
  updatedAt: string;
  optionGroups?: MenuOptionGroup[];
};

export type SelectedOption = {
  groupId: string;
  optionId: string;
  name: string;
  priceMinor: number;
};

export type CartLine = {
  productId: string;
  name: string;
  unitPriceMinor: number;
  quantity: number;
  options?: SelectedOption[];
  note?: string;
};

export type PaymentBreakdown = {
  cashMinor: number;
  cardMinor: number;
  cashReceivedMinor?: number;
  cardMode?: CardPaymentMode;
  cardReference?: string;
};

export type Order = {
  id: string;
  clientId: string;
  storeId: string;
  actorId: string;
  deviceId: string;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  currency: CurrencyCode;
  lines: CartLine[];
  subtotalMinor: number;
  discountMinor: number;
  discountByAmountMinor?: number;
  discountType?: DiscountType;
  discountTypeMinor?: number;
  totalMinor: number;
  paymentMethod: PaymentMethod;
  version: number;
  orderNo?: string;
  businessDate?: string;
  note?: string;
  payment?: PaymentBreakdown;
  discountReason?: string;
};

export type OrderEvent = {
  id: string;
  orderId?: string;
  type: CorrectionType | "no-sale" | "reprint" | "conflict-resolution";
  storeId: string;
  actorId: string;
  /** POS events carry a device; server-side Admin corrections may be actor-only. */
  deviceId?: string;
  occurredAt: string;
  amountMinor: number;
  paymentMethod?: PaymentMethod;
  refundMethod?: RefundMethod;
  reason?: string;
  conflictId?: string;
  resolutionAction?: ConflictResolutionAction;
  localPayloadHash?: string;
  remotePayloadHash?: string;
};

export type LocalPrintJob = {
  id: string;
  orderId: string;
  kind: PrintJobKind;
  status: PrintJobStatus;
  paperWidth: 58 | 80;
  createdAt: string;
  attemptCount: number;
  language?: "en" | "zh-Hant" | "zh-Hans";
  transport?: "web-serial" | "local-bridge" | "bluetooth" | "usb";
  lastError?: string;
  reprintOf?: string;
};

export type OrderMutation = {
  mutationId: string;
  entityType: "order";
  operation: "create";
  entityId: string;
  occurredAt: string;
  attemptCount: number;
  payload: Order;
};

export type EventMutation = {
  mutationId: string;
  entityType: "event";
  operation: "create";
  entityId: string;
  occurredAt: string;
  attemptCount: number;
  payload: OrderEvent;
};

export type OutboxMutation = OrderMutation | EventMutation;

export type SyncConflict = {
  id: string;
  orderId: string;
  detectedAt: string;
  reason: "pending-local-mutation" | "local-newer-than-server";
  localOrder: Order;
  remoteOrder: Order;
  resolvedAt?: string;
};

export type ConflictResolutionAction = "keep-server" | "accept-local-as-correction" | "reject-local";
export type ConflictStatus = "open" | "resolved" | "rejected";

export type ServerSyncConflict = SyncConflict & {
  storeId: string;
  localPayloadHash: string;
  remotePayloadHash: string;
  status: ConflictStatus;
  resolutionAction?: ConflictResolutionAction;
  resolutionReason?: string;
  resolvedBy?: string;
  resolvedDeviceId?: string;
  resolutionEventId?: string;
};

export type ConflictResolutionRequest = {
  action: ConflictResolutionAction;
  reason: string;
};

export type CreateOrderInput = {
  orderId: string;
  mutationId: string;
  clientId: string;
  storeId: string;
  actorId: string;
  deviceId: string;
  occurredAt: string;
  lines: CartLine[];
  currency?: CurrencyCode;
  discountMinor?: number;
  discountType?: DiscountType;
  paymentMethod: PaymentMethod;
  payment?: PaymentBreakdown;
  orderNo?: string;
  businessDate?: string;
  note?: string;
  discountReason?: string;
};

export type HealthResponse = {
  service: "posnext";
  status: "ok";
  phase: "foundation" | "offline-core" | "sync" | "identity";
};

export const POSNEXT_SERVICE = "posnext" as const;

function assertIntegerMinor(value: number, field: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${field} must be a non-negative integer minor unit`);
  }
}

export function discountTypeMinorFor(subtotalMinor: number, discountType: DiscountType = "none"): number {
  assertIntegerMinor(subtotalMinor, "subtotalMinor");
  const percentage = discountType === "10off" ? 10 : discountType === "20off" ? 20 : discountType === "none" ? 0 : undefined;
  if (percentage === undefined) throw new Error("Unsupported discount type");
  return Math.round(subtotalMinor * percentage / 100);
}

function assertLines(lines: CartLine[]): void {
  if (lines.length === 0) throw new Error("An order must contain at least one line");

  for (const line of lines) {
    if (!line.productId || !line.name) throw new Error("Order line identity is required");
    assertIntegerMinor(line.unitPriceMinor, "unitPriceMinor");
    for (const option of line.options ?? []) assertIntegerMinor(option.priceMinor, "option.priceMinor");
    if (!Number.isSafeInteger(line.quantity) || line.quantity < 1) {
      throw new Error("quantity must be a positive integer");
    }
  }
}

function normalizePayment(totalMinor: number, method: PaymentMethod, payment?: PaymentBreakdown): PaymentBreakdown {
  const normalized = payment ?? {
    cashMinor: method === "cash" ? totalMinor : 0,
    cardMinor: method === "card" ? totalMinor : 0,
  };
  assertIntegerMinor(normalized.cashMinor, "payment.cashMinor");
  assertIntegerMinor(normalized.cardMinor, "payment.cardMinor");
  if (normalized.cashReceivedMinor !== undefined) assertIntegerMinor(normalized.cashReceivedMinor, "payment.cashReceivedMinor");
  if (normalized.cashMinor + normalized.cardMinor !== totalMinor) throw new Error("Payment amounts must equal order total");
  if (method === "cash" && normalized.cardMinor !== 0) throw new Error("Cash payment cannot include card amount");
  if (method === "card" && normalized.cashMinor !== 0) throw new Error("Card payment cannot include cash amount");
  if (method === "mixed" && (normalized.cashMinor === 0 || normalized.cardMinor === 0)) throw new Error("Mixed payment requires cash and card amounts");
  if (normalized.cashReceivedMinor !== undefined && normalized.cashReceivedMinor < normalized.cashMinor) throw new Error("Cash received cannot be less than cash due");
  if (normalized.cardMinor > 0 && normalized.cardMode !== undefined && normalized.cardMode !== "manual") throw new Error("Unsupported card payment mode");
  return normalized;
}

export function createCompletedOrder(input: CreateOrderInput): {
  order: Order;
  mutation: OrderMutation;
} {
  assertLines(input.lines);
  const discountMinor = input.discountMinor ?? 0;
  assertIntegerMinor(discountMinor, "discountMinor");

  const lines = input.lines.map((line) => ({ ...line, options: line.options?.map((option) => ({ ...option })) }));
  const subtotalMinor = lines.reduce(
    (total, line) => total + (line.unitPriceMinor + (line.options ?? []).reduce((sum, option) => sum + option.priceMinor, 0)) * line.quantity,
    0,
  );
  if (!Number.isSafeInteger(subtotalMinor)) throw new Error("Order total exceeds safe integer range");
  const discountType = input.discountType ?? "none";
  const discountTypeMinor = discountTypeMinorFor(subtotalMinor, discountType);
  const totalDiscountMinor = discountMinor + discountTypeMinor;
  if (totalDiscountMinor > subtotalMinor) throw new Error("discountMinor cannot exceed subtotalMinor");
  const discountReason = input.discountReason?.trim();
  if (totalDiscountMinor > 0 && !discountReason) throw new Error("A discount reason is required when a discount is applied");
  const totalMinor = subtotalMinor - totalDiscountMinor;
  const payment = normalizePayment(totalMinor, input.paymentMethod, input.payment);

  const order: Order = {
    id: input.orderId,
    clientId: input.clientId,
    storeId: input.storeId,
    actorId: input.actorId,
    deviceId: input.deviceId,
    status: "completed",
    createdAt: input.occurredAt,
    updatedAt: input.occurredAt,
    currency: input.currency ?? "GBP",
    lines,
    subtotalMinor,
    discountMinor: totalDiscountMinor,
    discountByAmountMinor: discountMinor,
    discountType,
    discountTypeMinor,
    totalMinor,
    paymentMethod: input.paymentMethod,
    version: 1,
    orderNo: input.orderNo,
    businessDate: input.businessDate,
    note: input.note,
    discountReason,
    payment,
  };

  return {
    order,
    mutation: {
      mutationId: input.mutationId,
      entityType: "order",
      operation: "create",
      entityId: order.id,
      occurredAt: input.occurredAt,
      attemptCount: 0,
      payload: order,
    },
  };
}

export function createEventMutation(event: OrderEvent, mutationId: string): EventMutation {
  if (!event.id || !event.storeId || !event.actorId || !event.deviceId || !event.occurredAt) throw new Error("Event identity and timestamp are required");
  if (!Number.isSafeInteger(event.amountMinor) || event.amountMinor < 0) throw new Error("Event amount must be a non-negative integer minor unit");
  return {
    mutationId,
    entityType: "event",
    operation: "create",
    entityId: event.id,
    occurredAt: event.occurredAt,
    attemptCount: 0,
    payload: event,
  };
}

export type SyncPushRequest = {
  clientId: string;
  storeId: string;
  actorId: string;
  deviceId: string;
  mutations: OutboxMutation[];
  sync?: {
    clientStartedAt?: string;
    correlationId?: string;
    actionId?: string;
    retryOfActionId?: string;
  };
};

export type SyncPushResponse = {
  acceptedMutationIds: string[];
  alreadyAppliedMutationIds: string[];
  serverCursor: number;
  orderNumberAssignments?: Array<{ orderId: string; orderNo: string }>;
  conflictIds?: string[];
};

export type SyncPullResponse = {
  cursor: number;
  orders: Order[];
  events?: OrderEvent[];
  hasMore?: boolean;
  sync?: SyncPullTrace;
};

export type SyncPullTraceEntity = {
  entityType: "order" | "order_event";
  entityId: string;
};

export type SyncPullTrace = {
  correlationId: string;
  actionId: string;
  retryOfActionId?: string;
  cursor: number;
  entities: SyncPullTraceEntity[];
};

export type SyncPullAckRequest = {
  correlationId: string;
  actionId: string;
  retryOfActionId?: string;
  cursor: number;
  entities: SyncPullTraceEntity[];
  clientReceivedAt: string;
  clientAppliedAt: string;
  outcome: "success" | "failed";
  errorCode?: string;
};

export type SyncStatusResponse = {
  storeId: string;
  cursor: number;
  orderCount: number;
  eventCount: number;
};

export type CanonicalAuditEvent = {
  eventId: number;
  eventType: string;
  entityType: string;
  entityId: string;
  mutationId?: string;
  clientId?: string;
  storeId?: string;
  actorId?: string;
  deviceId?: string;
  occurredAt: string;
  recordedAt: string;
  payloadJson: string;
};

export type SalesSummary = {
  from: string;
  to: string;
  currency: CurrencyCode;
  orderCount: number;
  totalMinor: number;
  byPaymentMethod: Record<PaymentMethod, { orderCount: number; totalMinor: number }>;
  refundCount?: number;
  refundMinor?: number;
  netMinor?: number;
};
